import { z } from 'zod'
import { useSyncedState } from './useSyncedState'
import { useModelFloorIso } from './modelFloor'
import { fiscalYearStart } from '~/util/fiscalYear'
import {
  format,
  subDays,
  subYears,
  startOfYear,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
} from 'date-fns'

// Re-exported: `fiscalYearStart` moved to ~/util/fiscalYear (the chart layer needs it too, and
// this module is a hook). Kept exported here so existing importers and tests bind unchanged.
export { fiscalYearStart }

const presetKeySchema = z.enum([
  'last_4_weeks',
  'last_12_weeks',
  'last_quarter',
  'ytd',
  'fy_ytd',
  'last_year',
  'all_time',
  'custom',
])

export type TimeframePresetKey = z.infer<typeof presetKeySchema>

export interface TimeframePresetState {
  preset: TimeframePresetKey
  setPreset: (preset: TimeframePresetKey) => void
  isCustom: boolean
  computedRange: { fromIso: string; toIso: string } | null
}

/** Computes the {fromIso, toIso} window for `preset` relative to `today` (null for `custom`).
 *  `floorIso` is required: pass the DB-sourced model floor for `all_time`, or null if it hasn't
 *  loaded yet (returns null in that case — no silent fallback). Non-all_time presets ignore it.
 *  Exported for unit tests. */
export function computeRange(
  preset: TimeframePresetKey,
  today: Date,
  floorIso: string | null,
): { fromIso: string; toIso: string } | null {
  if (preset === 'custom') return null
  const toIso = format(today, 'yyyy-MM-dd')
  let fromDate: Date
  switch (preset) {
    case 'last_4_weeks':
      // FU-c: 7n inclusive — [today-27, today] = 28 days (subDays(28) made it 29, the 7n+1
      // root cause of row 18 / non-weekly panel splits).
      fromDate = subDays(today, 27)
      break
    case 'last_12_weeks':
      // FU-c: 7n inclusive — [today-83, today] = 84 days = exactly 12 weeks (subWeeks(12)
      // made it 85). Default gran_dots becomes ceil(84/4)=21 ⇒ 4 clean 3-week panels.
      fromDate = subDays(today, 83)
      break
    case 'last_quarter': {
      const lastQuarter = subQuarters(today, 1)
      return {
        fromIso: format(startOfQuarter(lastQuarter), 'yyyy-MM-dd'),
        toIso: format(endOfQuarter(lastQuarter), 'yyyy-MM-dd'),
      }
    }
    case 'ytd':
      fromDate = startOfYear(today)
      break
    case 'fy_ytd':
      // FISCAL year-to-date: [fiscal-year-start, anchor]. De-stales for free — `today` is the
      // WP-B anchor min(today, max_date). NOT covered by the calendar `ytd` preset.
      fromDate = fiscalYearStart(today)
      break
    case 'last_year':
      fromDate = subYears(today, 1)
      break
    case 'all_time':
      return floorIso ? { fromIso: floorIso, toIso } : null
    default:
      throw new Error(`Unhandled preset: ${preset satisfies never}`)
  }
  return { fromIso: format(fromDate, 'yyyy-MM-dd'), toIso }
}

export function useTimeframePresetState(
  anchorDate?: Date,
  defaultPreset: TimeframePresetKey = 'last_12_weeks',
): TimeframePresetState {
  const [preset, setPreset] = useSyncedState('tf_preset', presetKeySchema, 'url', defaultPreset)
  const anchor = anchorDate ?? new Date()
  const isCustom = preset === 'custom'
  const floorIso = useModelFloorIso()
  const computedRange = computeRange(preset, anchor, floorIso)
  return { preset, setPreset, isCustom, computedRange }
}
